import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

// ---------- 3 concept logos as pure SVG (scale to any size) ----------

// Option A: Storefront awning + QR pixels — literal + warm
function LogoA({ size = 96 }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} xmlns="http://www.w3.org/2000/svg" aria-label="Vendorly logo A">
      <rect width="100" height="100" rx="22" fill="#D97757" />
      {/* Awning stripes */}
      <path d="M20 32 Q50 20, 80 32 L80 42 L20 42 Z" fill="#FDFBF7" />
      <line x1="35" y1="21" x2="35" y2="42" stroke="#D97757" strokeWidth="2" />
      <line x1="50" y1="19" x2="50" y2="42" stroke="#D97757" strokeWidth="2" />
      <line x1="65" y1="21" x2="65" y2="42" stroke="#D97757" strokeWidth="2" />
      {/* Storefront body (implicit — bg) */}
      {/* QR finder pattern (customer scan symbol) */}
      <g transform="translate(30 52)">
        <rect x="0" y="0" width="16" height="16" rx="2" fill="none" stroke="#FDFBF7" strokeWidth="3" />
        <rect x="5" y="5" width="6" height="6" fill="#FDFBF7" />
      </g>
      {/* 2 more QR corners */}
      <rect x="60" y="52" width="10" height="10" rx="1.5" fill="#FDFBF7" />
      <rect x="55" y="72" width="6" height="6" rx="1" fill="#FDFBF7" />
      <rect x="68" y="72" width="10" height="6" rx="1" fill="#FDFBF7" />
    </svg>
  );
}

// Option B: Modern "V" with QR corner + speech bubble tail — abstract, memorable
function LogoB({ size = 96 }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} xmlns="http://www.w3.org/2000/svg" aria-label="Vendorly logo B">
      <rect width="100" height="100" rx="22" fill="#4A5D4E" />
      {/* Large V from two strokes */}
      <path d="M25 28 L50 72 L75 28" stroke="#FDFBF7" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      {/* QR finder pattern in top-right corner (accent) */}
      <g transform="translate(66 22)">
        <rect width="14" height="14" rx="2" fill="#D97757" />
        <rect x="3" y="3" width="8" height="8" rx="1" fill="#FDFBF7" />
        <rect x="5.5" y="5.5" width="3" height="3" fill="#D97757" />
      </g>
      {/* WhatsApp-style speech bubble tail at bottom */}
      <path d="M48 74 L50 84 L54 74 Z" fill="#FDFBF7" />
    </svg>
  );
}

// Option C: Chat bubble containing storefront awning + QR dot — friendliest
function LogoC({ size = 96 }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} xmlns="http://www.w3.org/2000/svg" aria-label="Vendorly logo C">
      <rect width="100" height="100" rx="22" fill="#FDFBF7" />
      {/* Chat bubble */}
      <path d="M18 30 Q18 20, 28 20 L72 20 Q82 20, 82 30 L82 62 Q82 72, 72 72 L46 72 L34 82 L34 72 L28 72 Q18 72, 18 62 Z"
            fill="#D97757" />
      {/* Awning inside bubble */}
      <path d="M28 38 Q50 30, 72 38 L72 45 L28 45 Z" fill="#FDFBF7" />
      <line x1="40" y1="32" x2="40" y2="45" stroke="#D97757" strokeWidth="1.5" />
      <line x1="50" y1="30" x2="50" y2="45" stroke="#D97757" strokeWidth="1.5" />
      <line x1="60" y1="32" x2="60" y2="45" stroke="#D97757" strokeWidth="1.5" />
      {/* QR finder mini-corner */}
      <rect x="35" y="52" width="12" height="12" rx="1.5" fill="none" stroke="#FDFBF7" strokeWidth="2.5" />
      <rect x="39" y="56" width="4" height="4" fill="#FDFBF7" />
      {/* accent dot */}
      <circle cx="60" cy="58" r="2.5" fill="#FDFBF7" />
      <circle cx="68" cy="58" r="2.5" fill="#FDFBF7" />
      <circle cx="60" cy="66" r="2.5" fill="#FDFBF7" />
      <circle cx="68" cy="66" r="2.5" fill="#FDFBF7" />
    </svg>
  );
}

// Wordmark: horizontal lock-up used in headers
function Wordmark({ Logo, label = "Vendorly" }) {
  return (
    <div className="flex items-center gap-3">
      <Logo size={44} />
      <span className="font-heading font-bold text-2xl tracking-tight text-brand-text">{label}</span>
    </div>
  );
}

const OPTIONS = [
  {
    key: "A",
    name: "The Kirana Awning",
    Logo: LogoA,
    story: "A classic Indian shop awning above a QR finder pattern. Warm terracotta says 'main street shop'. Literal, instantly recognisable in a WhatsApp thumbnail.",
    strength: "Best for print — QR posters, business cards, receipts",
  },
  {
    key: "B",
    name: "The Signal V",
    Logo: LogoB,
    story: "A modern 'V' in forest green with a terracotta QR corner accent and a subtle speech-bubble tail. Confident, tech-first, still warm.",
    strength: "Best for digital — clean at 16×16 favicon, works dark & light",
  },
  {
    key: "C",
    name: "The Shopkeeper's Chat",
    Logo: LogoC,
    story: "A speech bubble holding the shop awning + a QR dot pattern. Says 'WhatsApp for shops' at a glance. Softest, most on-brand for small vendors.",
    strength: "Best for landing page hero — tells the whole story in one mark",
  },
];

export default function LogoPreview() {
  return (
    <div className="min-h-screen bg-brand-bg text-brand-text">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-brand-bg/70 border-b border-brand-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="text-sm font-medium hover:text-brand-primary inline-flex items-center gap-1.5">
            <ArrowLeft size={14} weight="bold" /> Back to home
          </Link>
          <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary">Logo concepts</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-3">Pick your logo</h1>
        <p className="text-brand-text/70 max-w-2xl mb-14">Three directions, all built with your existing brand palette (terracotta + forest + cream). Reply <span className="font-semibold text-brand-text">"A"</span>, <span className="font-semibold text-brand-text">"B"</span>, or <span className="font-semibold text-brand-text">"C"</span> and I'll set it as your favicon + header logo everywhere.</p>

        <div className="grid md:grid-cols-3 gap-6">
          {OPTIONS.map(({ key, name, Logo, story, strength }) => (
            <div key={key} className="bg-white border border-brand-border rounded-3xl p-8 flex flex-col" data-testid={`logo-option-${key}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs uppercase tracking-[0.2em] text-brand-secondary">Option {key}</span>
                <span className="text-[10px] font-mono bg-brand-bg border border-brand-border rounded px-2 py-0.5">SVG · 96×96</span>
              </div>
              <h3 className="font-heading text-2xl font-bold tracking-tight mb-6">{name}</h3>

              {/* Big preview */}
              <div className="bg-brand-bg rounded-2xl p-8 flex items-center justify-center mb-4 border border-brand-border">
                <Logo size={128} />
              </div>

              {/* Wordmark on light + dark */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="bg-white rounded-xl p-3 border border-brand-border flex items-center justify-center min-h-[64px]">
                  <Wordmark Logo={Logo} />
                </div>
                <div className="bg-brand-text rounded-xl p-3 border border-brand-text flex items-center justify-center min-h-[64px]">
                  <div className="flex items-center gap-2">
                    <Logo size={36} />
                    <span className="font-heading font-bold text-lg tracking-tight text-white">Vendorly</span>
                  </div>
                </div>
              </div>

              {/* Favicon-size preview */}
              <div className="flex items-center gap-3 mb-4 text-xs text-brand-textMuted">
                <span className="uppercase tracking-widest">Favicon:</span>
                <Logo size={16} />
                <Logo size={24} />
                <Logo size={32} />
              </div>

              <p className="text-sm text-brand-text/80 leading-relaxed">{story}</p>
              <p className="mt-3 text-xs text-brand-secondary font-semibold">{strength}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-3xl bg-white border border-brand-border p-8 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-2">Choose one</div>
          <h3 className="font-heading text-2xl font-bold mb-1">Reply "A", "B", or "C"</h3>
          <p className="text-brand-text/70 text-sm">I'll swap the favicon, landing header, dashboard sidebar, and email templates to your pick — takes 5 minutes. You can always change your mind later.</p>
        </div>
      </main>
    </div>
  );
}
